import React, { useState, useEffect } from 'react';
import AppSidebar from '@/components/AppSidebar';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileDown, Activity, Menu, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOHReportPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (!token || !userData) {
        navigate('/login');
        return;
      }

      setUser(JSON.parse(userData));
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const assetsRes = await axios.get(`${API}/assets`, config);
      setAssets(assetsRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const downloadFleetReport = async () => {
    const toastId = toast.loading('Generating fleet SOH report...');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/soh-fleet/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 30000
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `soh_fleet_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Fleet SOH report downloaded', { id: toastId });
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download report', { id: toastId });
    }
  };

  const downloadAssetReport = async (assetId, assetName) => {
    const toastId = toast.loading(`Generating SOH report for ${assetName}...`);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/soh-asset/${assetId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 30000
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `soh_${assetName}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`SOH report for ${assetName} downloaded`, { id: toastId });
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download report', { id: toastId });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-black">Loading...</div>
    </div>;
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <AppSidebar />

      {/* Main Content */}
      <div className="flex-1">
        <header className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-gray-100 rounded-lg">
                <Menu className="w-6 h-6 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-black">State of Health Reports</h1>
                <p className="text-sm text-gray-600">Export fleet-wide and individual asset SOH reports</p>
              </div>
            </div>
            {user && <div className="text-sm text-gray-600">Welcome, {user.name}</div>}
          </div>
        </header>

        <main className="p-8">
          {/* Fleet Report */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Fleet-Wide State of Health Report
                <Button onClick={downloadFleetReport} className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white">
                  <FileDown className="w-4 h-4 mr-2" />
                  Download Fleet Report
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">Generate a comprehensive State of Health report for all {assets.length} assets in your fleet.</p>
            </CardContent>
          </Card>

          {/* Individual Asset Reports */}
          <Card>
            <CardHeader>
              <CardTitle>Individual Asset Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {assets.map(asset => (
                  <div key={asset.id} className="p-4 border border-gray-200 rounded-lg hover:border-red-500 transition-colors">
                    <h3 className="font-semibold text-black mb-2">{asset.name}</h3>
                    <div className="text-sm text-gray-600 space-y-1 mb-3">
                      <p>SOH: <span className="font-medium text-black">{asset.soh.toFixed(2)}%</span></p>
                      <p>Degradation: {asset.warranty_degradation_rate.toFixed(2)}%/yr</p>
                    </div>
                    <Button 
                      onClick={() => downloadAssetReport(asset.id, asset.name)}
                      variant="outline" 
                      size="sm" 
                      className="w-full border-red-500 text-red-600 hover:bg-red-50"
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      Download Report
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
};

export default SOHReportPage;
